/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1712880000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Apr 12 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "adjust",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Adjust",
          "depth": 0,
          "id": "connect-to-adjust"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      a: "a",
      ol: "ol",
      li: "li",
      h4: "h4",
      ul: "ul",
      code: "code",
      h3: "h3"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "S2S Events"
              })
            }), _jsx(_components.td, {
              children: "Sync records as events to Adjust. This is often in the form of a track call."
            }), _jsx(_components.td, {
              children: "Insert"
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-adjust",
        children: "Connect to Adjust"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), "\nbutton. Select ", _jsx(_components.strong, {
          children: "Adjust"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Adjust"
        }), " by entering the event token and app token:"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you do not have an event or app token, use the following steps from ", _jsx(_components.a, {
          href: "https://help.adjust.com/en/article/server-to-server-s2s-security#s2s-authentication",
          children: "Adjust"
        }), " to set up S2S authentication and generate them:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "AppView"
          }), ", select ", _jsx(_components.strong, {
            children: "All apps"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select your app."
        }), "\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Protection"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under the ", _jsx(_components.strong, {
            children: "S2S Security"
          }), " section, select ", _jsx(_components.strong, {
            children: "Open S2S Security"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Add token"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Enter a token name, and select ", _jsx(_components.strong, {
            children: "Add token"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You must also activate S2S Authentication with the following steps in ", _jsx(_components.a, {
          href: "https://help.adjust.com/en/article/server-to-server-s2s-security#activate-s2s",
          children: "Adjust"
        }), ":"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "AppView"
          }), ", select ", _jsx(_components.strong, {
            children: "All apps"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select your app."
        }), "\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Protection"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under the ", _jsx(_components.strong, {
            children: "S2S Security"
          }), " section, select ", _jsx(_components.strong, {
            children: "Open S2S Security"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Ensure you have at least one token configured."
        }), "\n", _jsxs(_components.li, {
          children: ["Switch the toggle ", _jsx(_components.strong, {
            children: "ON"
          }), ". A warning message appears to inform you that only requests sent with configured tokens will work."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Turn on"
          }), " to proceed."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've entered the event and app, click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". Give your destination a descriptive name and ", _jsx(_components.strong, {
          children: "Finish"
        }), " the destination connection setup."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Adjust destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull\ndata from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview\npage"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and\nthe Adjust destination you want to sync to."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Adjust requires all S2S events to have at least one device identifier. The device identifiers accepted for S2S events are as follows:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Raw IDFA (IOS only) (", _jsx(_components.code, {
            children: "idfa"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["Raw Google advertising ID (", _jsx(_components.code, {
            children: "gps_adid"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["Raw Amazon Fire advertising ID (", _jsx(_components.code, {
            children: "fire_adid"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["Open Advertising ID (OAID, Huawei only) (", _jsx(_components.code, {
            children: "oaid"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["Raw IDFV (", _jsx(_components.code, {
            children: "idfv"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["Android ID (", _jsx(_components.code, {
            children: "android_id"
          }), ")"]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Adjust recommends including the following parameters with events:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Adjust ID (", _jsx(_components.code, {
            children: "adid"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["IP address (", _jsx(_components.code, {
            children: "ip_address"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["Timestamp (", _jsx(_components.code, {
            children: "created_at"
          }), " or ", _jsx(_components.code, {
            children: "created_at_unix"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["User agent (", _jsx(_components.code, {
            children: "user_agent"
          }), ")"]
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "If you do not include the event timestamp, Adjust will use the time the event was received."
        })
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Hightouch accepts a normal date time format for the timestamp, and\nautomatically converts it to the format required by Adjust."
        })
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Adjust does not accept events more than 58 days old."
        })
      }), "\n", _jsx(_components.h4, {
        id: "custom-data",
        children: "Custom Data"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch allows mapping fields as ", _jsx(_components.code, {
          children: "callback_params"
        }), " or ", _jsx(_components.code, {
          children: "partner_params"
        }), " to send with Adjust events."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Callback parameters"
        }), " are available in your raw data exports and ", _jsx(_components.strong, {
          children: "partner parameters"
        }), " are shared with your network partners."]
      }), "\n", _jsx(_components.p, {
        children: "In Hightouch, type in the name of the field you would like included in these parameters, and choose a column to sync to it."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
